import React, { useMemo } from 'react';
import { useQuery } from 'react-query';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Button,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import {
  PersonAdd,
  Event,
  TrendingUp,
  Phone,
  Chat,
  CalendarMonth,
  School,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { leadsAPI, demosAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { formatDemoDualCaption, getTodayYMDInTimezone, getViewerTimezone, shiftLocalYMD } from '../../utils/dateTimeUtils';

const WIP_STATUSES = ['wip_before_demo', 'wip_after_demo', 'demo_scheduled', 'demo_reschedule_requested'];
const WIP_STATUS_IN = WIP_STATUSES.join(',');

const SalesDashboard = () => {
  const navigate = useNavigate();
  const { isSales, user } = useAuth();
  const canViewLeads = isSales();
  const isSalesRep = user?.role === 'sales';
  const myLeadFilter = isSalesRep && user?.id ? { assigned_to_list: 'me' } : {};
  const viewerTimezone = getViewerTimezone(user?.timezone);
  const today = getTodayYMDInTimezone(viewerTimezone);
  const weekStart = shiftLocalYMD(-6);

  const { data: leadsTodayRes, isLoading: leadsTodayLoading } = useQuery(
    ['leadsToday', today, myLeadFilter],
    () =>
      leadsAPI.getAll({
        ...myLeadFilter,
        lead_acquired_at_from: today,
        lead_acquired_at_to: today,
        limit: 50,
      }),
    { staleTime: 60000, enabled: canViewLeads }
  );

  const { data: wipRes, isLoading: wipLoading } = useQuery(
    ['wipLeads', myLeadFilter],
    () => leadsAPI.getAll({ ...myLeadFilter, status_in: WIP_STATUS_IN, limit: 50, page: 1 }),
    { staleTime: 60000, enabled: canViewLeads }
  );

  const { data: demosTodayRes, isLoading: demosTodayLoading } = useQuery(
    ['demosToday', today, viewerTimezone],
    () => demosAPI.getAll({ date_from: today, date_to: today, limit: 50, viewer_timezone: viewerTimezone }),
    { staleTime: 60000 }
  );
  const { data: conversionsRes, isLoading: conversionsLoading } = useQuery(
    ['conversionsWeek', weekStart, today, myLeadFilter],
    () =>
      leadsAPI.getAll({
        ...myLeadFilter,
        status: 'won',
        won_at_from: weekStart,
        won_at_to: today,
        limit: 1,
        page: 1,
      }),
    { staleTime: 60000, enabled: canViewLeads }
  );

  const leadsToday = leadsTodayRes?.data?.leads ?? [];
  const leadsTodayTotal = leadsTodayRes?.data?.pagination?.total ?? leadsToday.length;
  const demosToday = demosTodayRes?.data?.data ?? [];
  const demosTodayTotal = demosTodayRes?.data?.pagination?.total ?? demosToday.length;
  const conversionsTotal = conversionsRes?.data?.pagination?.total ?? 0;

  const wipLeads = useMemo(
    () => (wipRes?.data?.leads ?? []).filter((l) => WIP_STATUSES.includes(l.status)),
    [wipRes]
  );
  const wipTotal = wipRes?.data?.pagination?.total ?? wipLeads.length;

  const isLoading =
    demosTodayLoading ||
    (canViewLeads && (leadsTodayLoading || wipLoading || conversionsLoading));

  const StatCard = ({ title, value, icon, color, onClick }) => (
    <Card sx={{ height: '100%', cursor: onClick ? 'pointer' : 'default' }} onClick={onClick}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ bgcolor: color, borderRadius: 1, p: 1, color: 'white' }}>{icon}</Box>
            <Box>
              <Typography variant="h4" fontWeight="bold">
                {value}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {title}
              </Typography>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const actionButtons = (lead) => {
    const phone = lead.phone || '';
    const tel = phone ? `tel:${phone.replace(/\D/g, '')}` : null;
    const whatsapp = phone ? `https://wa.me/${phone.replace(/\D/g, '')}` : null;
    return (
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
        {tel && (
          <Button size="small" startIcon={<Phone />} href={tel} component="a">
            Call
          </Button>
        )}
        {whatsapp && (
          <Button size="small" startIcon={<Chat />} href={whatsapp} target="_blank" rel="noopener">
            WhatsApp
          </Button>
        )}
        <Button
          size="small"
          startIcon={<CalendarMonth />}
          onClick={() => navigate(`/demos?date_from=${today}&date_to=${today}`)}
        >
          Schedule Demo
        </Button>
        {lead.status !== 'won' && (
          <Button size="small" startIcon={<School />} onClick={() => navigate(`/leads?convert=${lead.id}`)}>
            Convert
          </Button>
        )}
      </Box>
    );
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Sales Dashboard
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        New leads, WIP follow-ups, demos and conversions at a glance.
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {canViewLeads ? (
          <>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="New leads today"
                value={leadsTodayTotal}
                icon={<PersonAdd />}
                color="#1976d2"
                onClick={() =>
                  navigate(`/leads?lead_acquired_at_from=${today}&lead_acquired_at_to=${today}`)
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="WIP follow-ups"
                value={wipTotal}
                icon={<Event />}
                color="#ed6c02"
                onClick={() => navigate(`/leads?status_in=${WIP_STATUS_IN}`)}
              />
            </Grid>
          </>
        ) : null}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Today's demos"
            value={demosTodayTotal}
            icon={<CalendarMonth />}
            color="#2e7d32"
            onClick={() => navigate(`/demos?date_from=${today}&date_to=${today}`)}
          />
        </Grid>
        {canViewLeads ? (
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Won this week"
              value={conversionsTotal}
              icon={<TrendingUp />}
              color="#9c27b0"
              onClick={() =>
                navigate(
                  `/leads?status=won&won_at_from=${weekStart}&won_at_to=${today}`
                )
              }
            />
          </Grid>
        ) : null}
      </Grid>

      {canViewLeads ? (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  New leads today
                </Typography>
                {leadsToday.length === 0 ? (
                  <Typography color="text.secondary">No new leads today.</Typography>
                ) : (
                  <List dense>
                    {leadsToday.slice(0, 8).map((lead) => (
                      <ListItem key={lead.id} divider>
                        <ListItemText
                          primary={`${lead.first_name || ''} ${lead.last_name || ''}`.trim() || lead.phone}
                          secondary={
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                              <Chip size="small" label={lead.status || 'new'} />
                              {lead.phone && (
                                <Typography variant="caption" color="text.secondary">
                                  {lead.phone}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                        <ListItemSecondaryAction>{actionButtons(lead)}</ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                )}
                <Button
                  size="small"
                  onClick={() =>
                    navigate(`/leads?lead_acquired_at_from=${today}&lead_acquired_at_to=${today}`)
                  }
                  sx={{ mt: 1 }}
                >
                  View all leads
                </Button>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Follow-ups (WIP)
                </Typography>
                {wipLeads.length === 0 ? (
                  <Typography color="text.secondary">No follow-ups in WIP.</Typography>
                ) : (
                  <List dense>
                    {wipLeads.slice(0, 8).map((lead) => (
                      <ListItem key={lead.id} divider>
                        <ListItemText
                          primary={`${lead.first_name || ''} ${lead.last_name || ''}`.trim() || lead.phone}
                          secondary={
                            <Chip size="small" label={lead.status || 'wip'} color="warning" />
                          }
                        />
                        <ListItemSecondaryAction>{actionButtons(lead)}</ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      ) : (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Today&apos;s demos
            </Typography>
            {demosToday.length === 0 ? (
              <Typography color="text.secondary">No demos scheduled today.</Typography>
            ) : (
              <List dense>
                {demosToday.slice(0, 10).map((demo) => (
                  <ListItem key={demo.id} divider>
                    <ListItemText
                      primary={demo.name || demo.title || demo.batch_name || 'Demo'}
                      secondary={formatDemoDualCaption(demo, viewerTimezone) || demo.status}
                    />
                  </ListItem>
                ))}
              </List>
            )}
            <Button
              size="small"
              onClick={() => navigate(`/demos?date_from=${today}&date_to=${today}`)}
              sx={{ mt: 1 }}
            >
              View demos
            </Button>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default SalesDashboard;
